/**
 * useConsumeSearchParam — operational continuity helper.
 *
 * Reads a single query-string param on mount, calls `onConsume` ONCE
 * with the value, then drops the param via setSearchParams(..., {replace}).
 * Returns the consumed value (or null) so callers can also trigger
 * downstream effects (e.g. scroll-to + brief highlight when the
 * receiving list has finished loading).
 *
 * This is the canonical pattern previously inlined inside WorkMode
 * for `?intent=`. Generalised here in Feb 28, 2026 as part of the
 * Operational Continuity Convergence Batch V1.
 *
 * Doctrine:
 *   • Pure read-then-drop. No persistent state. No new endpoint.
 *   • Fires the consume callback at most once per mount.
 *   • Never crashes on missing param — returns null and is a no-op.
 *   • Removes the param via `replace: true` so browser back/forward
 *     does not re-trigger the focus on every history navigation.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export function useConsumeSearchParam(name) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [consumed, setConsumed] = useState(null);

  useEffect(() => {
    const v = searchParams.get(name);
    if (!v) return;
    setConsumed(v);
    const next = new URLSearchParams(searchParams);
    next.delete(name);
    setSearchParams(next, { replace: true });
    // Intentional empty deps — single read-then-drop on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return consumed;
}

export default useConsumeSearchParam;
